import { z } from 'zod';

import type {
  ServerEntryChanges,
  ServerEntryInput,
  ServerTransport,
} from '@littlebird/axon-mcp';

const argsSchema = z.array(z.string()).optional();
const stringMapSchema = z.record(z.string(), z.string()).optional();
// A key with `=` in it reaches the child as a different variable
const envSchema = z
  .record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/), z.string())
  .optional();

const ServerTransportSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('stdio'),
    runtime: z.enum(['node', 'python']),
    packageSpec: z.string(),
    args: argsSchema,
    env: envSchema,
  }),
  z.object({
    kind: z.literal('binary'),
    command: z.string(),
    args: argsSchema,
    env: envSchema,
  }),
  z.object({
    kind: z.literal('http'),
    url: z.string(),
    headers: stringMapSchema,
  }),
  z.object({
    kind: z.literal('bundled'),
    serverId: z.string(),
    args: argsSchema,
    env: envSchema,
  }),
]) satisfies z.ZodType<ServerTransport>;

export const AddServerRequestSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  transport: ServerTransportSchema,
  template: z.string().optional(),
  setup: z
    .object({
      label: z.string(),
      args: argsSchema,
      env: envSchema,
      detailInTelemetry: z.boolean().optional(),
    })
    .optional(),
  lastConnectionTest: z
    .object({
      ok: z.boolean(),
      serverName: z.string().optional(),
      toolCount: z.number().optional(),
      testedAt: z.string(),
    })
    .optional(),
}) satisfies z.ZodType<ServerEntryInput>;

export const UpdateServerRequestSchema = z.object({
  slug: z.string(),
  changes: AddServerRequestSchema.partial().extend({
    enabled: z.boolean().optional(),
  }) satisfies z.ZodType<ServerEntryChanges>,
});

export const TestServerRequestSchema = z.object({
  transport: ServerTransportSchema,
  name: z.string().optional(),
  templateId: z.string().optional(),
});

export type LocalPathProblem =
  | 'missing'
  | 'permission_denied'
  | 'trailing_whitespace';

export type LocalPathCheckResult =
  | { ok: true }
  | { ok: false; problem: LocalPathProblem; path: string };

/** On-device templates whose arguments are folders the server serves */
export const LOCAL_PATH_TEMPLATE_IDS = ['filesystem', 'obsidian'] as const;

export type LocalPathTemplateId = (typeof LOCAL_PATH_TEMPLATE_IDS)[number];

export function isLocalPathTemplate(
  templateId: string | undefined
): templateId is LocalPathTemplateId {
  return (LOCAL_PATH_TEMPLATE_IDS as readonly string[]).includes(
    templateId ?? ''
  );
}
